<template>
  <div class="analytics-overview-views">
    <el-collapse v-model="active" class="analytics-overview-views__collapse">
      <el-collapse-item
        v-for="kpiKey in KPIKeys"
        :key="kpiKey"
        :name="kpiKey"
        class="analytics-overview-views__collapse-item"
      >
        <template #title>
          <div class="analytics-overview-views__collapse-item-title">
            <img :src="KPIIconPath[kpiKey]" />
            <span class="analytics-overview-views__collapse-header-title">
              {{ KPIText[kpiKey] }}
            </span>
            <span class="analytics-overview-views__collapse-header-count">{{
              kpiKey === KPIKeys.CTR
                ? totalNumberByKPI(kpiKey)
                : kpiKey === KPIKeys.EXPENSES || kpiKey === KPIKeys.AVERAGE_CPC
                ? formatCurrency(totalNumberByKPI(kpiKey), 'RUB')
                : numberWithSpacesCeilRound(totalNumberByKPI(kpiKey)).toLocaleString('ru-RU')
            }}</span>
          </div>
        </template>
        <ul class="analytics-overview-views__list">
          <li
            v-for="service in Object.keys(overviewAnalyticsData)"
            :key="service"
            class="analytics-overview-views__list-item"
          >
            <div class="analytics-overview-views__list-item-service-name">
              {{ getServiceName(service) }}
            </div>
            <div>
              {{
                kpiKey === KPIKeys.CTR
                  ? serviceKPIValue(service, kpiKey)
                  : kpiKey === KPIKeys.EXPENSES || kpiKey === KPIKeys.AVERAGE_CPC
                  ? formatCurrency(serviceKPIValue(service, kpiKey), getCurrencyCode(service))
                  : numberWithSpacesCeilRound(serviceKPIValue(service, kpiKey)).toLocaleString(
                      'ru-RU'
                    )
              }}
            </div>
          </li>
        </ul>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>
<script>
import { KPIKeys, KPIText, KPIKeysBackendMapper, AnalyticsType } from '@/utils/common';
import { getServiceName, ServiceType } from '@/utils/services';
import { formatCurrency } from '@/utils/helpers';
import IconCTR from '@/assets/icons/icon-ctr.svg';
import IconViews from '@/assets/icons/icon-views.svg';
import IconCost from '@/assets/icons/icon-cost.svg';
import IconKPI from '@/assets/icons/icon-kpi.svg';
import IconClicks from '@/assets/icons/icon-clicks.svg';
import IconConversions from '@/assets/icons/icon-conversions.svg';
import numberWithSpacesCeilRound from '@/mixin/numberWithSpaces';

const KPIIconPath = {
  [KPIKeys.IMPRESSIONS]: IconViews,
  [KPIKeys.CLICKS]: IconClicks,
  [KPIKeys.CTR]: IconCTR,
  [KPIKeys.AVERAGE_CPC]: IconKPI,
  [KPIKeys.EXPENSES]: IconCost,
  [KPIKeys.CONVERSIONS]: IconConversions,
};

export default {
  name: 'AnalyticsOverviewViews',
  components: {},
  mixins: [numberWithSpacesCeilRound],
  data() {
    return {
      active: [],
      AnalyticsType,
      ServiceType,
    };
  },
  computed: {
    KPIKeys() {
      return KPIKeys;
    },
    KPIText() {
      return KPIText;
    },
    KPIIconPath() {
      return KPIIconPath;
    },
    getCurrencyCode() {
      return (service) => {
        const s = service.toUpperCase();
        if (s === ServiceType.TELEGRAM) return 'EUR';
        if (s === ServiceType.GOOGLE) return 'USD';
        return 'RUB';
      };
    },
    analyticsSelectedOption() {
      return this.$store.state.Analytics[AnalyticsType.OVERVIEW].selectedOption;
    },
    overviewAnalyticsData() {
      return this.$store.state.Analytics[AnalyticsType.OVERVIEW].data || {};
    },
    activeAccount() {
      return this.$store.state.Auth.activeAccount;
    },
    currencyRates() {
      return this.$store.state.Auth.currencyRates;
    },
  },
  methods: {
    formatCurrency,
    getServiceName,
    getExchangeRate(from, to) {
      if (!this.currencyRates) return 1;
      const rate = this.currencyRates.find((el) => el.from === from && el.to === to);
      return rate ? rate.value : 1;
    },
    totalNumberByKPI(kpiKey) {
      if (!this.overviewAnalyticsData) return 0;

      // Получаем все данные с конвертацией валют
      // Изменение по валютам. На вкладках Обзор и Аккаунты и кампании выводим валюту площадки. НО когда мы суммируем значения с разных площадок, там мы берем курс на текущий момент, и переводим значения по Telegram из Евро в рубли, значения по Google из долларов в рубли. И уже рубли все суммируем в общее значение.
      let allItems = [];
      Object.entries(this.overviewAnalyticsData).forEach(([service, items]) => {
        if (service === 'telegram') {
          // Конвертируем Telegram из EUR в RUB
          const convertedItems = items.map((item) => ({
            ...item,
            total_cpc: Number(item.total_cpc || 0) * this.getExchangeRate('EUR', 'RUB'),
            total_spent: Number(item.total_spent || 0) * this.getExchangeRate('EUR', 'RUB'),
          }));
          allItems.push(...convertedItems);
        } else if (service === 'google') {
          // Конвертируем Google из USD в RUB
          const convertedItems = items.map((item) => ({
            ...item,
            total_cpc: Number(item.total_cpc || 0) * this.getExchangeRate('USD', 'RUB'),
            total_spent: Number(item.total_spent || 0) * this.getExchangeRate('USD', 'RUB'),
          }));
          allItems.push(...convertedItems);
        } else {
          // Остальные сервисы уже в рублях
          allItems.push(...items);
        }
      });

      if (kpiKey === KPIKeys.CTR) {
        const clicks = allItems.reduce((sum, item) => sum + Number(item.total_clicks ?? 0), 0);
        const views = allItems.reduce((sum, item) => sum + Number(item.total_views ?? 0), 0);
        const ctr = views ? (clicks / views) * 100 : 0;
        return ctr.toFixed(2) + '%';
      }

      if (kpiKey === KPIKeys.AVERAGE_CPC) {
        const spent = allItems.reduce((sum, item) => sum + Number(item.total_spent ?? 0), 0);
        const clicks = allItems.reduce((sum, item) => sum + Number(item.total_clicks ?? 0), 0);
        const cpc = clicks ? spent / clicks : 0;
        return cpc;
      }

      const field = KPIKeysBackendMapper[kpiKey];
      return allItems.reduce((sum, item) => {
        let val = item[field];
        if (typeof val === 'string') val = parseFloat(val.replace(',', '.'));
        if (isNaN(val)) val = 0;
        return sum + (val || 0);
      }, 0);
    },
    serviceKPIValue(service, kpiKey) {
      const arr = (this.overviewAnalyticsData && this.overviewAnalyticsData[service]) || [];
      if (kpiKey === KPIKeys.CTR) {
        const clicks = arr.reduce((sum, item) => sum + Number(item.total_clicks ?? 0), 0);
        const views = arr.reduce((sum, item) => sum + Number(item.total_views ?? 0), 0);
        const ctr = views ? (clicks / views) * 100 : 0;
        return ctr.toFixed(2) + '%';
      }

      if (kpiKey === KPIKeys.AVERAGE_CPC) {
        const spent = arr.reduce((sum, item) => sum + Number(item.total_spent ?? 0), 0);
        const clicks = arr.reduce((sum, item) => sum + Number(item.total_clicks ?? 0), 0);
        const cpc = clicks ? spent / clicks : 0;
        return cpc;
      }

      const field = KPIKeysBackendMapper[kpiKey];
      return arr.reduce((sum, item) => {
        let val = item[field];
        if (typeof val === 'string') val = parseFloat(val.replace(',', '.'));
        if (isNaN(val)) val = 0;
        return sum + (val || 0);
      }, 0);
    },
  },
  watch: {
    analyticsSelectedOption(value) {
      this.active = [value];
    },
    activeAccount: {
      async handler() {
        if (this.activeAccount && !this.currencyRates) {
          await this.$store.dispatch('Auth/getCurrencyRates', {
            business_account_id: this.activeAccount.id,
          });
        }
      },
    },
  },
  mounted() {
    if (this.analyticsSelectedOption) {
      this.active = [this.analyticsSelectedOption];
    }
    if (this.activeAccount && this.activeAccount.id) {
      this.$store.dispatch('Auth/getCurrencyRates', { business_account_id: this.activeAccount.id });
    }
  },
};
</script>
<style lang="scss" scoped>
@import '@/assets/styles/helpers/variables';
.analytics-overview-views {
  &__collapse-item-title {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  &__collapse {
    display: flex;
    flex-direction: column;
    gap: 12px;
    border: none;
  }

  &__list {
    margin-top: 6px;
  }

  &__list-item {
    display: flex;
    color: $gray;
    font-weight: 400;
    font-size: 12px;
    border-bottom: 1px solid $gray7;
    padding: 8px 0;
  }

  &__list-item-service-name {
    flex: 1 1 auto;
  }

  &__collapse-item ::v-deep .analytics-overview-views__collapse-header-title {
    flex: 1 1 auto;
    color: $basicBlack;
    font-weight: 400;
    font-size: 16px;
    line-height: 100%;
  }

  &__collapse-item ::v-deep .analytics-overview-views__collapse-header-count {
    color: $basicBlack;
    font-weight: 700;
    font-size: 16px;
    line-height: 100%;
  }

  &__collapse-item ::v-deep .el-collapse-item__header {
    display: flex;
    gap: 12px;
    height: 33px;
    line-height: 33px;
  }

  &__collapse-item ::v-deep .el-collapse-item__arrow {
    display: none;
  }
  &__collapse-item ::v-deep .el-collapse-item__wrap,
  &__collapse-item ::v-deep .el-collapse-item__header {
    border: none;
    background: transparent;
  }
}
</style>
